import { useSelector } from 'react-redux';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@design-system';
import { CheckCircle2, Send } from 'lucide-react';
import { selectClient } from '@client/model';
import { insuranceCarrierDefs } from '@shared/insuranceCarrierDefs';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSendAnonymousPui } from '../hooks/use-send-anonymous-pui';
import { SendPuiDialog } from './send-pui-dialog';

const SUPPORTED_CARRIERS = Object.values(insuranceCarrierDefs)
  .filter(carrier => carrier.supportsPUI === true)
  .map(carrier => carrier.name);

interface SendAnonymousPUIButtonProps {
  //custom size and variant for uniformity across parent component CTAs
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export function SendAnonymousPUIButton({
  variant = 'default',
  size = 'sm',
  className,
}: SendAnonymousPUIButtonProps) {
  const client = useSelector(selectClient);

  const {
    showDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleSendPui,
    isPending,
    isSuccess,
    puiSentData,
    availablePolicies,
  } = useSendAnonymousPui();
  const { flags } = useFeatureFlags();

  const displayPuiData =
    puiSentData && puiSentData.length > 0 ? puiSentData : client?.metadata?.anonymousPUI || [];

  const puiHistoryHoverElement = () => (
    <>
      {displayPuiData.length > 0 && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="cursor-pointer">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">PUI Sent History</h4>
              {displayPuiData.map((pui: PUIMetadata, index: number) => (
                <div
                  key={index}
                  className="border-b pb-2 last:border-b-0"
                >
                  <p className="text-sm font-medium">{pui.productName}</p>
                  <p className="text-xs text-gray-600">ID: {pui.anonymousIdentifier}</p>
                  <p className="text-xs text-gray-600">
                    Sent: {new Date(pui.sentDateTime).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600">
                    {pui.isJoint ? 'Joint Inquiry' : 'Individual Inquiry'}
                  </p>
                </div>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </>
  );

  if (!flags?.anonymousPUI) {
    return null;
  }

  return (
    <>
      <div>
        <TooltipProvider>
          <div className="flex items-center gap-2">
            {puiHistoryHoverElement()}
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handleOpenDialog}
                    disabled={isPending}
                    variant={variant}
                    size={size}
                    className={className}
                  >
                    <Send className="h-4 w-4" />
                    <span className="">{isPending ? 'Sending...' : 'Send Anonymous PUI'}</span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <p className="">
                    Send anonymized preliminary underwriting inquiries to{' '}
                    <span className="font-semibold">supported carriers:</span>
                  </p>
                  <ul className="list-inside list-disc space-y-0.5">
                    {SUPPORTED_CARRIERS.map(carrier => (
                      <li key={carrier}>{carrier}</li>
                    ))}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <SendPuiDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onSend={handleSendPui}
        isPending={isPending}
        isSuccess={isSuccess}
        availablePolicies={availablePolicies}
        sentPuiData={displayPuiData}
      />
    </>
  );
}
